import Length from "../../../../source/js/shared/models/Geometry/Length";
import DrillHole from "../DrillHole";
import { svgPathProperties } from "svg-path-properties";
import makerjs, {IModel, IPath, IPathLine, IPoint} from "makerjs";
import isEmpty from "lodash/isEmpty";
import has from "lodash/has";
import map from "lodash/map";

export default class Shape {
	protected parentModel: IModel;

	constructor( parentModel: IModel ) {
		this.parentModel = parentModel;
	}

	getOffsetModel( offset: Length ): IModel {

		// If there's no offset, we want to create them around the original model.
		if ( offset.in_cm() <= 0 ) {
			return { models: { 0: this.parentModel } };
		}

		return makerjs.model.outline( this.parentModel, offset.in_cm(), 1, true );
	}

	getPath( offset: Length ): object[] {
		let offsetModel = null;

		// We'll make 5 attempts to create an offset
		for( let i = 0; i < 5; i++ ) {
			offsetModel = this.getOffsetModel( offset.add( new Length( 0.0001 ).multiply( i ) ) );

			if ( ! isEmpty( offsetModel.models ) ) {
				break;
			}
		}

		if ( isEmpty( offsetModel ) || isEmpty( offsetModel.models ) || isEmpty( offsetModel.models[ 0 ].paths ) ) {
			return [];
		}

		return map( offsetModel.models[ 0 ].paths, ( entry, key ) => {
			entry.name = key;

			return entry;
		} );
	}

	getCorners( offset: Length, diameter: Length ): DrillHole[] {
		const resultPath = this.getPath( offset );

		return resultPath.map( ( point ) => {
			if ( ! has( point, 'origin' ) || ! has( point, 'end' ) ) {
				return;
			}

			return new DrillHole( Length.from_cm( point[ 'origin' ][ 0 ] ), Length.from_cm( point[ 'origin' ][ 1 ] ), diameter );
		} ).filter( element => ! isEmpty( element ) );
	}

	countEdges( offset: Length ): number {
		const resultPath = this.getPath( offset );

		return resultPath.length;
	}

	/**
	 * Generates drillholes around the inner perimeter of the shape.
	 */
	getAround( holeCounts: Array<number>, offset: Length, diameter: Length ): DrillHole[] {
		let resultPath = this.getPath( offset );

		if ( isEmpty( resultPath ) ) {
			return [];
		}

		resultPath = resultPath.reverse(); // Add holes clockwise manner.

		let previousPoint = resultPath.shift();
		resultPath.push( previousPoint );
		let holes = [];
		let edgeCount = 0;

		for ( const currentPoint of resultPath ) {
			const [ prevX, prevY ] = previousPoint[ 'origin' ];
			const [ curX, curY ] = currentPoint[ 'origin' ];

			const newLine = new svgPathProperties( `M ${ Length.from_cm( prevX ).in_mm() } ${ Length.from_cm( prevY ).in_mm() } L ${ Length.from_cm( curX ).in_mm() } ${ Length.from_cm( curY ).in_mm() }` );

			edgeCount++;
			previousPoint = currentPoint;

			if ( holeCounts[ edgeCount-1 ] == 1 ) {
				const lineLength = newLine.getTotalLength();
				const holePoint = newLine.getPointAtLength( ( lineLength / 2 ) );
				holes.push( new DrillHole( new Length( holePoint.x ), new Length( holePoint.y ), diameter, edgeCount ) );
				continue;
			}

			if ( holeCounts[ edgeCount-1 ] < 1 ) {
				continue;
			}

			for ( let index = 0; index < holeCounts[ edgeCount-1 ]; index++ ) {
				const holeCount = holeCounts[ edgeCount-1 ];
				const lineLength = newLine.getTotalLength();
				const holePoint = newLine.getPointAtLength( ( lineLength/( holeCount-1 ) )*index );
				holes.push( new DrillHole( new Length( holePoint.x ), new Length( holePoint.y ), diameter, edgeCount ) );
			}
		}
		holes = this.deduplicate( holes );
		return holes;
	}

	deduplicate( drillholes: DrillHole[] ): DrillHole[] {
		const found = [];
		const deduplicated = [];
		for ( const drillhole of drillholes ) {
			if ( !found.includes( ( Math.round( drillhole.x.in_mm() * 100 ) / 100 +'-'+ Math.round( drillhole.y.in_mm() * 100 ) / 100 ) ) ) {
				deduplicated.push( drillhole );
				found.push( ( Math.round( drillhole.x.in_mm() * 100 ) / 100 +'-'+ Math.round( drillhole.y.in_mm() * 100 ) / 100 ) );
			}
		}
		return deduplicated;
	}

	createEdgePath( holeCounts: number[], offsetTopBottom: Length, offsetLeftRight: Length, startingPoint: Length ): IModel {
		let i = 0;
		const mapped = map( this.parentModel.paths, ( entry, key ) => {
			if ( holeCounts[ i ] === 0 || entry.type !== 'line' ) {
				return false;
			}

			const newEntry = this.createOffsettedLine( entry, i, offsetLeftRight, offsetTopBottom, startingPoint );

			newEntry[ 'name' ] = key;

			i++;

			return newEntry;
		} ).filter( ( item: boolean|IPath ) => item !== false );

		const cloned = makerjs.cloneObject( {
			paths: { ...mapped }
		} );

		if ( isEmpty( cloned.paths ) || Object.keys( cloned.paths ).length === 1 ) {
			return {};
		}

		return cloned;
	}

	createOffsettedLine( path: IPathLine, index: number, offsetLeftRight: Length, offsetTopBottom: Length, startingPoint: Length ): IModel {
		const cloned = makerjs.cloneObject( path );

		// Bottom Left to Bottom Right
		if ( index === 0 ) {
			cloned.origin[ 0 ] = startingPoint.in_cm();
			cloned.origin[ 1 ] = offsetTopBottom.in_cm();

			cloned.end[ 0 ] = cloned.end[ 0 ] - startingPoint.in_cm();
			cloned.end[ 1 ] = offsetTopBottom.in_cm();
		}

		// Bottom Right to Top Right
		if ( index === 1 ) {
			cloned.origin[ 0 ] = cloned.origin[ 0 ] - offsetLeftRight.in_cm();
			cloned.origin[ 1 ] = startingPoint.in_cm();

			cloned.end[ 0 ] = cloned.end[ 0 ] - offsetLeftRight.in_cm();
			cloned.end[ 1 ] = cloned.end[ 1 ] - startingPoint.in_cm();
		}

		// Top Right to Top Left
		if ( index === 2 ) {
			cloned.origin[ 0 ] = cloned.origin[ 0 ] - startingPoint.in_cm();
			cloned.origin[ 1 ] = cloned.origin[ 1 ] - offsetTopBottom.in_cm();

			cloned.end[ 0 ] = startingPoint.in_cm();
			cloned.end[ 1 ] = cloned.end[ 1 ] - offsetTopBottom.in_cm();
		}

		// Top Left to Bottom Right
		if ( index === 3 ) {
			cloned.origin[ 0 ] = offsetLeftRight.in_cm();
			cloned.origin[ 1 ] = cloned.origin[ 1 ] - startingPoint.in_cm();

			cloned.end[ 0 ] = offsetLeftRight.in_cm();
			cloned.end[ 1 ] = startingPoint.in_cm();
		}

		return cloned;
	}

	fromEdge( holeCounts: number[], offsetTopBottom: Length, offsetLeftRight: Length, diameter: Length, startingPoint: Length ): DrillHole[] {
		let holes = [];

		const edgePath = this.createEdgePath( holeCounts, offsetTopBottom, offsetLeftRight, startingPoint );

		if ( isEmpty( edgePath ) ) {
			return holes;
		}

		for ( const [ key, side ] of Object.entries( edgePath.paths ) ) {
			const holesPerSide = holeCounts[ key ];
			const points = makerjs.path.toPoints( side as IPath, holesPerSide );

			points.forEach( function ( coordinates: IPoint ) {
				holes.push( new DrillHole( Length.from_cm( coordinates[ 0 ] ), Length.from_cm( coordinates[ 1 ] ), diameter ) );
			} );
		}

		holes = this.deduplicate( holes );

		return holes;
	}
}
